import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';
import { useCollectStore } from '@/stores/collect';
import { useEncounterStore } from '@/stores/encounter';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useRegistrationStore } from '@/stores/registration';
import { buildCollectPayload } from '@/utils/collectPayload';
import { isNetworkError } from '@/utils/networkError';
import { logger } from '@/logging/logger';
import { LogCategory } from '@/logging/types';
import { telemetrySync } from '@/logging/telemetrySync';

export interface GenericEventPayload {
  name: string;
  data?: Record<string, any>;
  additions?: string[];
}

export interface SendOptions {
  clinicalNoteText?: string | null;
  encounterId?: string | null;
}

export type CollectResult = { ok: true; outcome: 'accepted' | 'queued' } | { ok: false };

// vuex actions reached sentry through the store's root vue instance, which pinia has no
// equivalent for. the sentry plugin reaches the app off the same global
const sentry = (): any => (window as any).App?.$sentry;

// Name and message only: an error object can carry the request body.
const errorShape = (e: unknown): Record<string, unknown> => ({
    type: (e as Error)?.name,
    reason: (e as Error)?.message,
});

export async function genericEvent(payload: GenericEventPayload): Promise<void> {
    const collectStore = useCollectStore()
    const registrationStore = useRegistrationStore()
    const partnerConfigStore = usePartnerConfigStore();
    try {
        let dataOptions: Record<string, any> = {
            globalSequence: collectStore.globalSequence,
            encountersInQueue: collectStore.recordsInQueue,
            encounterId: useEncounterStore().uuid
        }

        let data = payload.data || {}

        if (payload.additions) {
            data = payload.additions.reduce((acc: Record<string, any>, cur) => {
                acc[cur] = dataOptions[cur]
                return acc
            }, data)
        }

        let content = {
            name: payload.name,
            device: {
                engagement: partnerConfigStore.code,
                registrationId: registrationStore.registrationId,
                hrId: registrationStore.hrId,
            },
            data: data
        }

        const options = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(content)
        }
        await fetch('api/v3/device/event', options)
    }
    catch (e) {
        logger.warn(
            LogCategory.SYNC,
            'sync.event.failed',
            { name: payload.name, ...errorShape(e) },
            'Device event did not reach the server',
        ).then()
    }
}

export async function sendCollect(payload: SendOptions = {}): Promise<CollectResult> {
    const collectStore = useCollectStore();
    const partnerConfigStore = usePartnerConfigStore();
    const startedAt = Date.now();
    let results: CollectResult = { ok: false }
    try {
        if (collectStore.ready) {

            collectStore.incrementTrackCounter()
            // Set before the payload build: the only guard against the un-awaited sendCollect.
            collectStore.uploaded = true

            let body = await buildCollectPayload({ deIdentify: true, ...payload });

            collectStore.incrementSequenceCounter(body.track.name)

            if (partnerConfigStore.webhookUrl != null) {
                await sendCustomerData(payload);
            }

            const options = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            }

            const response = await fetch('api/v3/collect', options)

            if (response.ok && response.headers?.get('X-ThinkMD-Collect') === 'queued') {
                results = { ok: true, outcome: 'queued' }
            }
            else if (response.ok) {
                results = { ok: true, outcome: 'accepted' }
                collectStore.setLastUploadDate()

                logger.info(
                    LogCategory.SYNC,
                    'sync.collect.completed',
                    {
                        recordsInQueue: collectStore.recordsInQueue,
                        trackCount: collectStore.track,
                        durationMs: Date.now() - startedAt,
                    },
                    'Encounter upload accepted',
                ).then()
                telemetrySync.requestDrain('upload').then()
            }
            else {
                collectStore.uploaded = false
                logger.error(
                    LogCategory.SYNC,
                    'sync.collect.rejected',
                    { status: response.status, durationMs: Date.now() - startedAt },
                    'Encounter upload rejected by the server',
                ).then()
            }
        }
        else {
            // Submitted, not accepted: the flag is set before the fetch and the worker owns retry.
            logger.info(
                LogCategory.SYNC,
                'sync.collect.skipped',
                { reason: 'already_submitted', recordsInQueue: collectStore.recordsInQueue },
                'Encounter upload skipped',
            ).then()
        }
    }
    catch (e: any) {
        // Workbox 6 returns a receipt for queued requests. A rejected fetch has
        // no successful receipt, so keep the encounter available for retry.
        collectStore.uploaded = false

        logger.error(
            LogCategory.SYNC,
            'sync.collect.failed',
            { ...errorShape(e), network: isNetworkError(e) },
            'Encounter upload did not reach the server',
        ).then()

        if (!isNetworkError(e)) {
            sentry()?.withScope((scope: any) => {
                scope.setTag("location", "prep")
                sentry().captureException(e)
            });
        }
    }
    return results
}

export async function sendCustomerData(payload: SendOptions = {}): Promise<void> {
    const partnerConfigStore = usePartnerConfigStore()

    let body = await buildCollectPayload({ deIdentify: false, ...payload });
    let publicKey = partnerConfigStore.publicKey;
    let trustedServers = partnerConfigStore.trustedServers;

    var exportPayload: any = null;

    if (publicKey) {
        // 1. Generate a random symmetric key (e.g., 32 bytes)
        const symmetricKey = CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex);
        const iv = CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex); // Initialization Vector

        // 2. Encrypt your large JSON data with AES
        const jsonData = JSON.stringify(body);
        const encryptedData = CryptoJS.AES.encrypt(jsonData, CryptoJS.enc.Hex.parse(symmetricKey), {
            iv: CryptoJS.enc.Hex.parse(iv)
        }).toString();

        // 3. Encrypt the symmetric key and IV with the RSA public key
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(publicKey);

        const combinedKeys = JSON.stringify({ key: symmetricKey, iv: iv });
        const encryptedSymmetricKey = encrypt.encrypt(combinedKeys);

        // 4. Check for success and send the payload
        if (encryptedData && encryptedSymmetricKey) {
            exportPayload = {
                contentType: 'application/json',
                data: btoa(JSON.stringify({
                    encryptedKey: encryptedSymmetricKey,
                    encryptedData: encryptedData
                }))
            };
        }
    }
    else {
        exportPayload = {
            contentType: 'application/json',
            data: btoa(JSON.stringify(body))
        };
    }

    if (trustedServers != null) {
        const servers = trustedServers.split(',');

        for (const trustedServer of servers) {
            try {
                await fetch(`https://${trustedServer}/api/v3/export`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Engagement-Code': partnerConfigStore.code
                    },
                    body: JSON.stringify(exportPayload),
                    credentials: 'include'
                });
            } catch (e) {
                // destination only: the trusted server host is partner infrastructure.
                logger.warn(
                    LogCategory.SYNC,
                    'sync.export.failed',
                    { destination: 'trusted', ...errorShape(e) },
                    'Customer data export did not reach the server',
                ).then()
            }

            // only the first trusted server is used
            break;
        }
    }
    else {
        try {
            await fetch('api/v3/export', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Engagement-Code': partnerConfigStore.code
                },
                body: JSON.stringify(exportPayload)
            });
        } catch (e) {
            logger.warn(
                LogCategory.SYNC,
                'sync.export.failed',
                { destination: 'default', ...errorShape(e) },
                'Customer data export did not reach the server',
            ).then()
        }
    }
}
